/**
 * 收款入口对话框（ADM-10，payment-entry-lifecycle:SC-26/27/28），UI 对齐原型 PaymentEntryModal。
 *
 * 按订单+节点+类型查询既有入口（含已支付，取最新非 CLOSED，不重复生成，SC-27）；无则按需「生成」
 * （SC-26，无金额入参，SC-01）。可付金额由「入口授权 + 订单款项形态」派生展示（对齐原型
 * paymentEntryPayableOptions 裁决：绑定/待收节点仅一项；未定形态按授权展示首付款或全款）。
 * 首笔款项入口支付前可修改分期授权（SC-28）。
 */
import { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Popconfirm, Space, Spin, Tag, Typography, message } from 'antd';
import { CopyOutlined, LinkOutlined, QrcodeOutlined } from '@ant-design/icons';
import { DraggableModal } from '../../components/DraggableModal';
import { paymentEntriesApi } from '../../api/payment-entries.api';
import { RECEIVABLE_TYPE_LABEL } from '../../api/billing.api';
import { fmtDateTime } from '../../utils/datetime';
import {
  PAYMENT_ENTRY_TYPE_LABEL,
  type PaymentEntryOrderContext,
  type PaymentEntryType,
  type PaymentEntryView,
  type PaymentOption,
} from '../../types/payment.types';

const { Text, Paragraph } = Typography;

export interface PaymentEntryDialogProps {
  open: boolean;
  orderId: string;
  /** 尾款/待收节点场景绑定的应收节点；首笔款项（形态未定）为 undefined。 */
  receivablePlanId?: string;
  entryType: PaymentEntryType;
  /** 订单款项上下文（挂载点从订单详情传入），派生可付金额展示；缺省时金额展示为「-」。 */
  orderContext?: PaymentEntryOrderContext;
  onClose: () => void;
  /** 支付状态变化回调（mock 模拟支付成功后通知挂载点刷新订单/收款状态）。 */
  onPaid?: () => void;
}

/** 分→元展示（与原型 formatAmount 一致，无千分位）。 */
function formatYuan(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

/** 生成时间展示（本地时区经 fmtDateTime 换算，禁止直接截 UTC 串）；缺省不抛，避免生成结果缺 createdAt 时白屏。 */
function formatDateTime(value: string | undefined): string {
  if (!value) return '—';
  return fmtDateTime(value);
}

/**
 * 入口当前可付款项（对齐原型 derive.ts paymentEntryPayableOptions）：
 * 入口已绑节点 → 该节点待付；订单有待收节点 → 该节点待付；
 * 款项未定 → 无入口（生成前，家长自选）或已授权分期为首付款+全款两项，未授权仅全款。
 */
export function paymentEntryPayableOptions(
  entry: Pick<PaymentEntryView, 'receivablePlanId' | 'allowDeposit' | 'receivablePlanSummary'> | null,
  orderContext?: PaymentEntryOrderContext,
): PaymentOption[] {
  if (entry?.receivablePlanSummary) {
    const summary = entry.receivablePlanSummary;
    return [{ type: summary.type, amount: summary.amount - summary.paidAmount, receivablePlanId: summary.receivablePlanId }];
  }
  if (orderContext?.currentReceivablePlan) {
    const plan = orderContext.currentReceivablePlan;
    return [{ type: plan.type, amount: plan.amountMinor - plan.paidAmountMinor }];
  }
  if (!orderContext) return [];
  const fullOption: PaymentOption = { type: 'FULL', amount: orderContext.receivableAmountMinor };
  if (!entry || entry.allowDeposit) {
    return [
      { type: 'DEPOSIT', amount: Math.round(orderContext.receivableAmountMinor * orderContext.depositRatio) },
      fullOption,
    ];
  }
  return [fullOption];
}

/** 款项选项展示文案：单项 ¥X；多项「首付款 ¥X 或 全款 ¥Y」（对齐原型 Modal 组装）。 */
function formatPayableOptions(options: PaymentOption[]): string {
  if (options.length === 0) return '-';
  if (options.length === 1) return formatYuan(options[0]!.amount);
  return options.map((option) => `${RECEIVABLE_TYPE_LABEL[option.type]} ${formatYuan(option.amount)}`).join(' 或 ');
}

export function PaymentEntryDialog({
  open,
  orderId,
  receivablePlanId,
  entryType,
  orderContext,
  onClose,
  onPaid,
}: PaymentEntryDialogProps) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [mockPaying, setMockPaying] = useState(false);
  const [entry, setEntry] = useState<PaymentEntryView | null>(null);
  const [error, setError] = useState('');
  /** 生成前的分期授权选择（首笔款项可勾选；尾款恒全款）。 */
  const [allowDeposit, setAllowDeposit] = useState(false);

  const isLink = entryType === 'LINK';
  const title = PAYMENT_ENTRY_TYPE_LABEL[entryType];
  // 挂载场景 receivablePlanId 即当前待收节点；尾款节点不提供分期授权
  const isFinalPlan = orderContext?.currentReceivablePlan?.type === 'FINAL';

  useEffect(() => {
    if (!open || !orderId) return;
    void loadExisting();
  }, [open, orderId, receivablePlanId, entryType]);

  async function loadExisting(): Promise<void> {
    setLoading(true);
    setError('');
    setEntry(null);
    try {
      const list = await paymentEntriesApi.listPaymentEntries({ orderId });
      // SC-27：匹配类型+节点的最新非 CLOSED 入口直显（含已支付），不重复生成
      const match = list
        .filter((e) => {
          const status = e.derivedStatus ?? e.status;
          return (
            e.entryType === entryType &&
            (e.receivablePlanId ?? '') === (receivablePlanId ?? '') &&
            status !== 'CLOSED'
          );
        })
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
      if (match) setEntry(match);
    } catch (e) {
      setError(e instanceof Error ? e.message : '入口查询失败');
    } finally {
      setLoading(false);
    }
  }

  async function copyToken(token: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(token);
      message.success('已复制访问凭证');
    } catch {
      message.error('复制失败，请手动复制');
    }
  }

  async function handleGenerate(): Promise<void> {
    setGenerating(true);
    setError('');
    try {
      // SC-01：无金额入参；尾款（isFinalPlan）恒不授权分期，其余按勾选项
      const created = await paymentEntriesApi.createPaymentEntry({
        orderId,
        entryType,
        receivablePlanId,
        allowDeposit: isFinalPlan ? false : allowDeposit,
      });
      setEntry(created);
      message.success(`${title}已生成`);
      if (created.token) await copyToken(created.token);
    } catch (e) {
      setError(e instanceof Error ? e.message : '生成失败');
    } finally {
      setGenerating(false);
    }
  }

  async function handleAllowDepositChange(checked: boolean): Promise<void> {
    if (!entry) return;
    try {
      await paymentEntriesApi.updateAllowDeposit(entry.id, {
        allowDeposit: checked,
      });
      setEntry({ ...entry, allowDeposit: checked });
      message.success(checked ? '已授权家长可只付首付款' : '已收回分期授权，家长仅可付全款');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '授权修改失败');
    }
  }

  /** mock 通道一键模拟家长扫码支付（IKBFM3：复用生产结算链，幂等）。 */
  async function handleMockPay(): Promise<void> {
    if (!entry) return;
    setMockPaying(true);
    try {
      const result = await paymentEntriesApi.mockPayPaymentEntry(entry.id);
      if (result.status === 'PAID') {
        setEntry({ ...entry, status: 'PAID', derivedStatus: 'PAID' });
        message.success(result.idempotent ? '该入口此前已支付（幂等复用）' : '已模拟家长支付并完成入账');
        onPaid?.();
      } else {
        message.error(`模拟支付未完成：${result.failure ?? '结算链失败'}`);
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : '模拟支付失败');
    } finally {
      setMockPaying(false);
    }
  }

  const options = paymentEntryPayableOptions(entry, orderContext);
  const amountText = formatPayableOptions(options);
  // 生成前 Checkbox 的首付金额（原型：receivableAmount × depositRatio）
  const depositAmountText = orderContext
    ? formatYuan(Math.round(orderContext.receivableAmountMinor * orderContext.depositRatio))
    : null;
  // 未绑定节点且未支付的入口允许事后修改分期授权（CLOSED 不会入选）
  const canEditAllowDeposit = Boolean(entry) && !entry!.receivablePlanId && entry!.status !== 'PAID';

  return (
    <DraggableModal
      open={open}
      title={title}
      onCancel={onClose}
      footer={null}
      width={420}
      destroyOnHidden
      data-testid="payment-entry-dialog"
    >
      <Spin spinning={loading || generating}>
        {error && <div style={{ color: '#c0392b', marginBottom: 16 }}>{error}</div>}

        {!entry && !loading && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Paragraph>
              <Text type="secondary">该{receivablePlanId ? '应收节点' : '订单'}尚未生成{title}</Text>
            </Paragraph>
            {/* 非尾款款项生成前可选择授权分期（原型：仅 isFinalPlan 隐藏） */}
            {!isFinalPlan && (
              <Paragraph style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={allowDeposit}
                  onChange={(event) => setAllowDeposit(event.target.checked)}
                  data-testid="payment-entry-allow-deposit-checkbox"
                >
                  允许家长只付首付款{depositAmountText ? `（${depositAmountText}，否则需一次性结清全款）` : '（否则需一次性结清全款）'}
                </Checkbox>
              </Paragraph>
            )}
            <Button
              type="primary"
              icon={isLink ? <LinkOutlined /> : <QrcodeOutlined />}
              loading={generating}
              onClick={() => void handleGenerate()}
            >
              生成
            </Button>
          </div>
        )}

        {entry && (
          <div style={{ textAlign: 'center' }}>
            {isLink ? (
              <>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text type="secondary">生成时间：{formatDateTime(entry.createdAt)}</Text>
                </Paragraph>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    readOnly
                    value={entry.token}
                    onFocus={(event) => event.target.select()}
                    data-testid="payment-entry-token-input"
                  />
                  <Button
                    type="primary"
                    icon={<CopyOutlined />}
                    onClick={() => void copyToken(entry.token)}
                  >
                    复制
                  </Button>
                </Space.Compact>
                <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    金额 {amountText}，仅订单归属监护人手机号可访问
                  </Text>
                </Paragraph>
              </>
            ) : (
              <>
                {/* 服务端下发二维码（IKBFM3：真图为扫码入口；后端未返回时回落占位）。
                    qrImageUrl 为 /api 开头的同源相对路径，<img> 直连不走 transport。 */}
                {entry.qrImageUrl ? (
                  <img
                    src={entry.qrImageUrl}
                    alt="收款二维码"
                    width={180}
                    height={180}
                    style={{ margin: '12px auto', borderRadius: 8, display: 'block' }}
                    data-testid="payment-entry-qr-image"
                  />
                ) : (
                  <div
                    style={{
                      width: 180,
                      height: 180,
                      margin: '12px auto',
                      background: 'repeating-linear-gradient(45deg,#102a25,#102a25 10px,#fff 10px,#fff 20px)',
                      borderRadius: 8,
                    }}
                    data-testid="payment-entry-qr-placeholder"
                  />
                )}
                <Paragraph style={{ marginBottom: 4 }}>
                  <Text type="secondary">
                    家长微信扫码进入收款页，金额 {amountText}
                    {entry.paymentChannel === 'mock' && (
                      <Tag color="orange" style={{ marginLeft: 8 }} data-testid="payment-entry-mock-channel-tag">
                        mock 通道
                      </Tag>
                    )}
                  </Text>
                </Paragraph>
                <Paragraph style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    生成时间：{formatDateTime(entry.createdAt)}
                  </Text>
                </Paragraph>
                <Space>
                  <Button
                    type="primary"
                    icon={<CopyOutlined />}
                    onClick={() => void copyToken(entry.token)}
                  >
                    复制
                  </Button>
                  {entry.paymentChannel === 'mock' && entry.derivedStatus !== 'PAID' && entry.derivedStatus !== 'CLOSED' && (
                    <Popconfirm
                      title="模拟家长扫码支付？"
                      description="将按生产链路完成下单、入账与权益开通（幂等，可重复触发）"
                      okText="确定"
                      cancelText="取消"
                      onConfirm={() => void handleMockPay()}
                    >
                      <Button loading={mockPaying} data-testid="payment-entry-mock-pay-button">
                        模拟支付成功
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </>
            )}
            {canEditAllowDeposit && (
              <Paragraph style={{ marginTop: 16, marginBottom: 0 }}>
                <Checkbox
                  checked={entry.allowDeposit}
                  onChange={(event) => void handleAllowDepositChange(event.target.checked)}
                >
                  允许家长只付首付款
                </Checkbox>
              </Paragraph>
            )}
          </div>
        )}
      </Spin>
    </DraggableModal>
  );
}
